import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Box, Typography, Paper, Chip, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import TechnikaServiceApi from '../api/JsdhNotifikaceService/service/TechnikaService';
import JsdhNotifikaceService from '../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import type { Udalost, Technika, Odpoved, UcastConfig, PoplachTechnikaResponse, DisplejStylOdpovedi } from '../api/JsdhNotifikaceService/generated';
import { QueryError } from '../api/QueryError';
import TechnikaFoto from './TechnikaFoto';
import DojezdOdpocet from './DojezdOdpocet';
import { FitHeightBox } from './FitHeightBox';
import { useNestihaVyjezd } from '../hooks/useNestihaVyjezd';
import { tintNestiha } from '../theme/nestihaTint';
import { tintZaloha, zalohaTextNaPodkladu } from '../theme/zalohaBarva';
import { useZalohaJinde } from '../hooks/useZalohaJinde';
import { rozdelOdpovedi, jmenoZalohy } from '../utils/rozdelOdpovedi';
import { ZalohaPilulka } from './ZalohaPilulka';

interface TechnikaUcastWrapperProps {
    aktivniPoplach: Udalost;
    deviceToken?: string;
    ucastConfig?: UcastConfig;
    displejStyl?: DisplejStylOdpovedi;
    adaptivniLayout?: boolean;
}

const UCAST_CARD_SX = {
    p: 0.75,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 1.5,
    flex: '1 1 calc(33.333% - 8px)',
    maxWidth: 'calc(33.333% - 6px)',
    minWidth: 200,
} as const;

// Od 4 vozů jsou karty ve mřížce o dvou řádcích ve 2/3 šířky řady (karta odpovědí zůstává
// vpravo ve třetině). Počet sloupců mřížky je omezen šířkou: karta užší než MIN_KARTA_MRIZKA_PX
// by volací znak při 16 px nevešla vedle fotky.
const MIN_KARTA_MRIZKA_PX = 240;
const RADKY_MRIZKY = 2;
const VYCHOZI_MAX_SLOUPCU = 3;

export const maxSloupcuMrizky = (sirkaRadyPx: number): number =>
    sirkaRadyPx > 0
        ? Math.max(1, Math.floor((sirkaRadyPx * 2) / 3 / MIN_KARTA_MRIZKA_PX))
        : VYCHOZI_MAX_SLOUPCU;

const MRIZKA_CARD_SX = {
    p: 0.75,
    display: 'flex',
    flexDirection: 'row',
    gap: 1,
    minWidth: 0,
} as const;

const getTechnikaCardSx = (count: number) => ({
    p: 0.75,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: count === 3 ? 1 : 1.5,
    flex: count === 3 ? '1 1 calc(22.222% - 8px)' : '1 1 calc(33.333% - 8px)',
    maxWidth: count === 3 ? 'calc(22.222% - 6px)' : 'calc(33.333% - 6px)',
    minWidth: count === 3 ? 150 : 200,
}) as const;

const TechnikaUcastWrapper: React.FC<TechnikaUcastWrapperProps> = ({
    aktivniPoplach,
    deviceToken,
    ucastConfig,
    displejStyl = 'STRIPE',
    adaptivniLayout = false,
}) => {
    const showSemafor = ucastConfig?.semafor !== false;
    const showRole = ucastConfig?.role !== false;
    const showSpecializace = ucastConfig?.specializace !== false;
    const isResponzivni = ucastConfig?.responzivni !== false;

    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const stripeJedu = '#2e7d32';
    const stripeNejedu = '#c62828';
    const sectionTintJedu = isDark ? 'rgba(76, 175, 80, 0.12)' : 'rgba(46, 125, 50, 0.10)';
    const sectionTintNejedu = isDark ? 'rgba(244, 67, 54, 0.12)' : 'rgba(198, 40, 40, 0.10)';
    const cardTintJedu = isDark ? 'rgba(76, 175, 80, 0.22)' : 'rgba(46, 125, 50, 0.18)';
    const cardTintNejedu = isDark ? 'rgba(244, 67, 54, 0.22)' : 'rgba(198, 40, 40, 0.18)';

    const podkladNestiha = tintNestiha(theme, 0.18);

    const poplachId = useMemo(() => aktivniPoplach?.id, [aktivniPoplach?.id]);

    // Query na techniku
    const { data: allTechnika } = useQuery<Technika[]>({
        queryKey: ['technika', 'active', deviceToken],
        queryFn: () =>
            deviceToken
                ? TechnikaServiceApi.getActiveTechnikaWithDeviceToken(deviceToken)
                : TechnikaServiceApi.getActiveTechnika(),
        // Kiosk bez obsluhy — viz DashboardPage: bez intervalu zustane seznam po jednom
        // selhanem nacteni prazdny az do rucniho reloadu stranky.
        refetchInterval: 15000,
        staleTime: 10000,
        refetchOnWindowFocus: false,
    });

    // Query na odpovědi (polling 4s)
    const { data: odpovedi = [] } = useQuery<Odpoved[], QueryError>({
        queryKey: ['odpovedi', poplachId, deviceToken],
        queryFn: () =>
            poplachId
                ? JsdhNotifikaceService.getOdpovediByPoplachId(poplachId, deviceToken || undefined)
                : Promise.resolve([]),
        enabled: Boolean(poplachId),
        refetchInterval: 4000,
        refetchOnWindowFocus: false,
        staleTime: 3000,
    });

    // Zaloha z predchoziho poplachu (spec R6) — odpovedi si wrapper taha sam, takze i zalohu.
    const zalohaJinde = useZalohaJinde(poplachId, deviceToken);
    const rozdeleni = useMemo(() => rozdelOdpovedi(odpovedi, zalohaJinde), [odpovedi, zalohaJinde]);

    // HO-162: hasic, jehoz ohlaseny dojezd je po limitu vyjezdu jednotky, je oranzovy
    // do sveho ohlaseneho prijezdu. Jen jedouci — zaloha uz je ve zbrojnici.
    const jeNestihajici = useNestihaVyjezd(aktivniPoplach.casVyhlaseni, rozdeleni.jedou);

    // Query na detaily techniky s info o zdroji (KOPIS/VELITEL)
    const { data: technikaSources } = useQuery<PoplachTechnikaResponse[]>({
        queryKey: ['udalost-technika', poplachId, deviceToken],
        queryFn: () => JsdhNotifikaceService.getUdalostTechnika(poplachId!, deviceToken || undefined),
        enabled: Boolean(poplachId),
        refetchInterval: 5000,
        refetchOnWindowFocus: false,
        staleTime: 4000,
    });

    const sourceMap = useMemo(() => {
        const map = new Map<number, PoplachTechnikaResponse>();
        technikaSources?.forEach(t => map.set(t.technikaId, t));
        return map;
    }, [technikaSources]);

    // Filtrovat techniku podle ID z poplach_technika
    const matchedTechnika = useMemo(() => {
        if (!allTechnika || !aktivniPoplach.technikaIds?.length) return [];
        return allTechnika.filter(t => aktivniPoplach.technikaIds!.includes(t.id));
    }, [allTechnika, aktivniPoplach.technikaIds]);

    // Šířka řady → kolik sloupců karet se vejde do mřížky (od 4 vozů).
    const radaRef = useRef<HTMLDivElement>(null);
    const [sirkaRady, setSirkaRady] = useState(0);
    useEffect(() => {
        const el = radaRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => setSirkaRady(el.clientWidth));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const jeMrizka = matchedTechnika.length > 3;
    const maxViditelnych = jeMrizka ? RADKY_MRIZKY * maxSloupcuMrizky(sirkaRady) : 3;
    const viditelneVozy = matchedTechnika.slice(0, maxViditelnych);
    const technikaCount = viditelneVozy.length;
    const sloupceMrizky = Math.ceil(technikaCount / RADKY_MRIZKY);
    const isWide = isResponzivni && technikaCount <= 1;

    // Rozdělit odpovědi na JEDU / NEJEDU + spočítat role
    const { jedouOdpovedi, nejedouOdpovedi, pocetVD, pocetS, pocetH } = useMemo(() => {
        const jedouOdpovedi = rozdeleni.jedou;
        const nejedouOdpovedi = rozdeleni.nejedou;
        let pocetVD = 0, pocetS = 0, pocetH = 0;
        for (const o of jedouOdpovedi) {
            if (o.funkce) {
                const kody = o.funkce.map(f => f.kod);
                if (kody.includes('VD')) pocetVD++;
                if (kody.includes('S')) pocetS++;
                if (kody.includes('H')) pocetH++;
            }
        }
        return { jedouOdpovedi, nejedouOdpovedi, pocetVD, pocetS, pocetH };
    }, [rozdeleni]);

    // Adaptivní layout — větší font v každé skupině zvlášť (sekce s ≤ 4 členy se zvětší)
    const ADAPTIVE_THRESHOLD = 4;
    const useAdaptiveJedu = adaptivniLayout && jedouOdpovedi.length <= ADAPTIVE_THRESHOLD;
    const useAdaptiveNejedu = adaptivniLayout && nejedouOdpovedi.length <= ADAPTIVE_THRESHOLD;

    const jmenoFontFor = (large: boolean) => large ? '1.4rem' : (isWide ? '1.1rem' : '1rem');
    const chipFontSizeFor = (large: boolean) => large ? '0.8rem' : (isWide ? '0.65rem' : '0.5rem');
    const chipHeightFor = (large: boolean) => large ? 22 : (isWide ? 18 : 14);
    const itemPadYFor = (large: boolean) => large ? 0.6 : (isWide ? 0.5 : 0.25);

    const maZalohu = rozdeleni.zaloha.length > 0;
    const barvaZalohy = zalohaTextNaPodkladu(theme);
    const MAX_JMEN_ZALOHY = 3;
    const jmenaZalohy = rozdeleni.zaloha.slice(0, MAX_JMEN_ZALOHY).map(jmenoZalohy).join(', ');
    const prebytekZalohy = rozdeleni.zaloha.length - MAX_JMEN_ZALOHY;

    // Sekce wrap (vnější Box kolem JEDU/NEJEDU sloupce) — žádné pozadí (COLUMNS_TINTED odstraněno)
    const sectionWrapSx = (_jedu: boolean) => ({});

    // Item style (vnitřní Box s jménem)
    // STRIPE má barevný borderLeft proužek;
    // BACKGROUND_TINTED má jemný barevný background u jména + proužek navíc.
    // flexWrap: 'nowrap' zaručuje, že chip s funkcí/specializací zůstane na stejném řádku jako jméno.
    const itemStyleSx = (jedu: boolean, large: boolean, nestiha = false) => {
        const barvaProuzku = nestiha ? theme.palette.warning.main : (jedu ? stripeJedu : stripeNejedu);
        const base = {
            px: 1,
            py: itemPadYFor(large),
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap' as const,
            gap: 0.5,
            breakInside: 'avoid' as const,
            borderRadius: 0.5,
            borderLeft: '3px solid',
            borderLeftColor: barvaProuzku,
            minWidth: 0,
        };
        // Oranzovy podklad i ve STRIPE: "cela kolonka oranzove" (HO-162)
        if (nestiha) return { ...base, backgroundColor: podkladNestiha };
        if (displejStyl === 'BACKGROUND_TINTED') {
            return { ...base, backgroundColor: jedu ? cardTintJedu : cardTintNejedu };
        }
        return base;
    };

    // Dynamicky vypocitat kolik jmen se vejde do karty odpovedi
    const odpovediRef = useRef<HTMLDivElement>(null);
    const [maxItemsJedu, setMaxItemsJedu] = useState(20);
    const [maxItemsNejedu, setMaxItemsNejedu] = useState(20);

    useEffect(() => {
        const el = odpovediRef.current;
        if (!el) return;
        const observer = new ResizeObserver(() => {
            // Měří se přímo oblast jmen (nad řádkem rolí), ne celá karta: odhad výšky řádku rolí
            // a paddingu konstantami se rozešel se skutečností (role mají 16 px) a poslední jméno
            // pak bylo bez náznaku napůl pod hranou karty.
            const h = el.clientHeight;
            // item: fontSize*1.5 lineHeight + py + mb 4px
            // adaptive: 1.4rem*1.5=33px + 9.6px py + 4px mb ≈ 47px (reálně ~50 px)
            // wide: 1.1rem*1.5=26px + 8px py + 4px mb ≈ 38px
            // normal: 1rem*1.5=24px + 4px py + 4px mb = 32px
            // Vyska radku se pocita pro kazdy sloupec zvlast: sloupec, ktery se vykresluje kompaktne,
            // se nesmi merit radkem zvetsenym kvuli druhemu sloupci (jinak se ukaze min jmen, nez se vejde).
            const radekH = (adaptive: boolean) => (adaptive ? 52 : (isWide ? 38 : 32));
            // spodní mb posledního řádku (4 px) se do výšky vejít nemusí
            const kolik = (adaptive: boolean) => Math.max(1, Math.floor((h + 4) / radekH(adaptive)));
            // Radek zalohy pod seznamem Jedou zabira jeden kompaktni radek.
            setMaxItemsJedu(Math.max(1, kolik(useAdaptiveJedu) - (maZalohu ? 1 : 0)));
            setMaxItemsNejedu(kolik(useAdaptiveNejedu));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [isWide, useAdaptiveJedu, useAdaptiveNejedu, maZalohu]);

    // Vozy, které se do mřížky nevejdou (víc než 2 řádky × sloupce podle šířky), se nesmí
    // ztratit potichu: vypíšou se aspoň textem nad řadou.
    const skryteVozy = matchedTechnika.slice(maxViditelnych);

    const kartaVozu = (t: Technika) => {
        const source = sourceMap.get(t.id);
        const nazevVozu = (
            <Typography data-testid="ucast-nazev" sx={{
                fontSize: '1em', fontWeight: 600, color: 'text.primary', lineHeight: 1.25, overflowWrap: 'break-word',
                // ve mřížce nejvýš 2 řádky s „…" (celé řádky, žádný napůl uříznutý třetí)
                // flexShrink 0: s overflow hidden by ho flex sloupec jinak zmáčkl pod výšku řádku a FitHeightBox
                // by přetečení nepoznal (písmo se nezmenší, z názvu zbude proužek)
                ...(jeMrizka && { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', flexShrink: 0 }),
            }}>
                {t.nazev}
            </Typography>
        );
        const spzVozu = t.spz && (
            <Typography sx={{ fontSize: 'max(16px, 0.8em)', color: 'text.secondary', lineHeight: 1.3, mt: 0.25 }}>
                {t.spz}
            </Typography>
        );
        const chipVozu = source && (
            <Box>
            <Chip
                size="small"
                label={
                    source.zdroj === 'KOPIS'
                        ? 'KOPIS'
                        : source.potvrzenoKopis
                            ? `Velitel ✓ KOPIS`
                            : 'Velitel'
                }
                sx={{
                    mt: 0.5,
                    height: 'auto',
                    fontWeight: 700,
                    fontSize: 'max(16px, 0.75em)',
                    color: '#fff',
                    bgcolor: source.zdroj === 'KOPIS'
                        ? 'info.main'
                        : source.potvrzenoKopis
                            ? 'success.main'
                            : 'warning.main',
                }}
            />
            </Box>
        );
        return (
            <Paper key={t.id} elevation={1} data-testid="ucast-technika-karta"
                sx={{ ...(jeMrizka ? MRIZKA_CARD_SX : getTechnikaCardSx(technikaCount)), minHeight: 0, overflow: 'hidden', alignItems: 'stretch' }}>
                {/* Písmo se zmenší podle výšky karty (min. 16 px); potomci jsou v em. */}
                <FitHeightBox maxFontPx={jeMrizka || technikaCount === 3 ? 28 : 34} fitKey={`${t.volaciZnak}|${t.nazev}|${t.spz}|${source?.zdroj}|${technikaCount}`}
                    sx={{ flex: '1 1 0', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', justifyContent: 'safe center' }}>
                    <Typography data-testid="ucast-volaci" sx={{ fontSize: '1.9em', fontWeight: 900, color: 'error.main', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                        {t.volaciZnak}
                    </Typography>
                    {/* Ve mřížce (od 4 vozů) je karta poloviční výšky: SPZ a chip jsou hned pod volacím
                        znakem na jednom řádku a název vozu až za nimi, takže když se při 16 px nevejde
                        všechno, zkrátí se jen název (s náznakem „…"), ne SPZ ani chip. */}
                    {!jeMrizka && nazevVozu}
                    {jeMrizka ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1 }}>
                            {spzVozu}
                            {chipVozu}
                        </Box>
                    ) : (
                        <>
                            {spzVozu}
                            {chipVozu}
                        </>
                    )}
                    {jeMrizka && nazevVozu}
                </FitHeightBox>
                <TechnikaFoto
                    technikaId={t.id}
                    maFotografii={t.maFotografii}
                    alt={t.nazev}
                    deviceToken={deviceToken}
                    objectFit="contain"
                    ikonaFontSize={jeMrizka || technikaCount === 3 ? 48 : 64}
                    sx={{ width: jeMrizka ? '38%' : '45%', maxHeight: '100%', aspectRatio: '4/3', flex: '0 0 auto', minWidth: 0, alignSelf: 'center' }}
                />
            </Paper>
        );
    };

    return (
        // Vyplní zbytek výšky pod detailem poplachu (Layout je flex sloupec); karty mají výšku
        // řady, ne poměr stran fotky, takže řada ani počítadla nepřetečou pod okraj monitoru.
        <Box data-testid="ucast-sekce" sx={{ p: 1, flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {skryteVozy.length > 0 && (
                <Typography data-testid="ucast-dalsi-vozy" sx={{ flex: '0 0 auto', fontSize: '1rem', fontWeight: 700, color: 'text.secondary', lineHeight: 1.2 }}>
                    + další {skryteVozy.length === 1 ? 'vůz' : 'vozy'}: {skryteVozy.map(t => t.volaciZnak).join(', ')}
                </Typography>
            )}
            <Box ref={radaRef} data-testid="ucast-rada" sx={{ display: 'flex', gap: 1, alignItems: 'stretch', flex: '1 1 0', minHeight: 0 }}>
                {/* Karty techniky: do 3 vozů v jedné řadě, od 4 vozů mřížka 2 řádky × sloupce */}
                {jeMrizka ? (
                    <Box data-testid="ucast-mrizka" sx={{
                        flex: '2 1 0', minWidth: 0, minHeight: 0,
                        display: 'grid', gap: 1,
                        gridTemplateColumns: `repeat(${sloupceMrizky}, minmax(0, 1fr))`,
                        gridTemplateRows: `repeat(${RADKY_MRIZKY}, minmax(0, 1fr))`,
                    }}>
                        {viditelneVozy.map(kartaVozu)}
                    </Box>
                ) : viditelneVozy.map(kartaVozu)}

                {/* Prázdné sloty aby karta odpovědí byla vždy ve třetím (pravém) slotu */}
                {!isResponzivni && technikaCount === 0 && (
                    <>
                        <Box sx={{ flex: '1 1 calc(33.333% - 8px)', maxWidth: 'calc(33.333% - 6px)', minWidth: 200 }} />
                        <Box sx={{ flex: '1 1 calc(33.333% - 8px)', maxWidth: 'calc(33.333% - 6px)', minWidth: 200 }} />
                    </>
                )}
                {!isResponzivni && technikaCount === 1 && (
                    <Box sx={{ flex: '1 1 calc(33.333% - 8px)', maxWidth: 'calc(33.333% - 6px)', minWidth: 200 }} />
                )}

                {/* Karta odpovědí */}
                <Paper
                    elevation={1}
                    sx={{
                        ...UCAST_CARD_SX,
                        // Vedle mřížky (2/3 šířky) má karta odpovědí pevně třetinu.
                        ...(jeMrizka && { flex: '1 1 0', maxWidth: 'none' }),
                        ...(isWide && technikaCount === 1 && {
                            flex: '1 1 calc(66.666% - 8px)',
                            maxWidth: 'calc(66.666% - 6px)',
                        }),
                        ...(isWide && technikaCount === 0 && {
                            flex: '1 1 100%',
                            maxWidth: '100%',
                        }),
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        position: 'relative',
                        overflow: 'hidden',
                        minHeight: 0,
                    }}
                >
                    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', p: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* Horní část: jména + semafor */}
                        <Box ref={odpovediRef} data-testid="ucast-odpovedi-jmena" sx={{ flex: 1, display: 'flex', gap: 1, minHeight: 0, overflow: 'hidden' }}>
                            {/* JEDU sloupec */}
                            <Box sx={{ flex: 1, minWidth: 0, ...(isWide && { columns: 2, columnGap: '8px' }), ...sectionWrapSx(true) }}>
                                {((isWide ? jedouOdpovedi.length > maxItemsJedu * 2 : jedouOdpovedi.length > maxItemsJedu)
                                    ? jedouOdpovedi.slice(0, (isWide ? maxItemsJedu * 2 : maxItemsJedu) - 1)
                                    : jedouOdpovedi
                                ).map(o => {
                                    const nestiha = jeNestihajici(o);
                                    return (
                                        <Box
                                            key={o.id}
                                            data-testid="ucast-jedu-polozka"
                                            data-nestiha={nestiha ? 'true' : 'false'}
                                            sx={itemStyleSx(true, useAdaptiveJedu, nestiha)}
                                        >
                                            <Typography fontSize={jmenoFontFor(useAdaptiveJedu)} fontWeight="bold" noWrap sx={{ minWidth: 0, flexShrink: 1 }}>
                                                {o.jmeno}
                                            </Typography>
                                            {o.dojezdCas && aktivniPoplach.casVyhlaseni && (
                                                <DojezdOdpocet
                                                    dojezdCas={o.dojezdCas}
                                                    casVyhlaseni={aktivniPoplach.casVyhlaseni}
                                                    fontSize={jmenoFontFor(useAdaptiveJedu)}
                                                />
                                            )}
                                            {showSpecializace && o.specializace?.map(s => (
                                                <Chip key={s.id} label={s.kod} size="small" variant="outlined" color="secondary"
                                                      sx={{ fontSize: chipFontSizeFor(useAdaptiveJedu), height: chipHeightFor(useAdaptiveJedu), '& .MuiChip-label': { px: 0.5 } }} />
                                            ))}
                                        </Box>
                                    );
                                })}
                                {(isWide ? jedouOdpovedi.length > maxItemsJedu * 2 : jedouOdpovedi.length > maxItemsJedu) && (
                                    <Typography fontSize="1rem" color="text.secondary" sx={{ pl: 1 }}>
                                        + {jedouOdpovedi.length - (isWide ? maxItemsJedu * 2 : maxItemsJedu) + 1} dalších
                                    </Typography>
                                )}
                                {maZalohu && (
                                    <Box
                                        data-testid="zaloha-radek"
                                        title={rozdeleni.zaloha.map(jmenoZalohy).join(', ')}
                                        sx={{
                                            columnSpan: 'all', mt: 0.5, px: 1, py: 0.25, borderRadius: 0.5,
                                            backgroundColor: tintZaloha(theme, 0.14), color: barvaZalohy,
                                            fontSize: '1rem', fontWeight: 800,
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}
                                    >
                                        V ZÁLOZE {rozdeleni.zaloha.length}: {jmenaZalohy}{prebytekZalohy > 0 ? ` +${prebytekZalohy}` : ''}
                                    </Box>
                                )}
                            </Box>
                            {/* NEJEDU sloupec */}
                            <Box sx={{ flex: 1, minWidth: 0, ...(isWide && { columns: 2, columnGap: '8px' }), ...sectionWrapSx(false) }}>
                                {((isWide ? nejedouOdpovedi.length > maxItemsNejedu * 2 : nejedouOdpovedi.length > maxItemsNejedu)
                                    ? nejedouOdpovedi.slice(0, (isWide ? maxItemsNejedu * 2 : maxItemsNejedu) - 1)
                                    : nejedouOdpovedi
                                ).map(o => (
                                    <Box
                                        key={o.id}
                                        sx={itemStyleSx(false, useAdaptiveNejedu)}
                                    >
                                        <Typography fontSize={jmenoFontFor(useAdaptiveNejedu)} fontWeight="bold" noWrap sx={{ minWidth: 0, flexShrink: 1 }}>
                                            {o.jmeno}
                                        </Typography>
                                        {showSpecializace && o.specializace?.map(s => (
                                            <Chip key={s.id} label={s.kod} size="small" variant="outlined" color="secondary"
                                                  sx={{ fontSize: chipFontSizeFor(useAdaptiveNejedu), height: chipHeightFor(useAdaptiveNejedu), '& .MuiChip-label': { px: 0.5 } }} />
                                        ))}
                                    </Box>
                                ))}
                                {(isWide ? nejedouOdpovedi.length > maxItemsNejedu * 2 : nejedouOdpovedi.length > maxItemsNejedu) && (
                                    <Typography fontSize="1rem" color="text.secondary" sx={{ pl: 1 }}>
                                        + {nejedouOdpovedi.length - (isWide ? maxItemsNejedu * 2 : maxItemsNejedu) + 1} dalších
                                    </Typography>
                                )}
                            </Box>
                            {/* Počítadlo sloupec */}
                            {showSemafor && (
                            <Box sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, minWidth: isWide ? 100 : 80 }}>
                                <Box sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    bgcolor: 'success.main',
                                    color: 'white',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 0.5,
                                    px: 1,
                                    flex: 1,
                                }}>
                                    <Typography fontWeight="bold" fontSize="1rem" lineHeight={1}>
                                        JEDU
                                    </Typography>
                                    <Typography fontWeight="bold" fontSize={isWide ? '3.5rem' : '3rem'} lineHeight={1} mt={0.75}>
                                        {jedouOdpovedi.length}
                                    </Typography>
                                    <ZalohaPilulka pocet={rozdeleni.zaloha.length} fontSize={isWide ? '0.85rem' : '0.75rem'} />
                                </Box>
                                <Box sx={{
                                    width: '100%',
                                    borderRadius: 2,
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 0.5,
                                    px: 1,
                                    flex: 1,
                                }}>
                                    <Typography fontWeight="bold" fontSize="1rem" lineHeight={1}>
                                        NEJEDU
                                    </Typography>
                                    <Typography fontWeight="bold" fontSize={isWide ? '3.5rem' : '3rem'} lineHeight={1} mt={0.75}>
                                        {nejedouOdpovedi.length}
                                    </Typography>
                                </Box>
                            </Box>
                            )}
                        </Box>
                        {/* Spodní řádek: role */}
                        {showRole && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flex: '0 0 auto' }}>
                            {[
                                { label: 'Velitel družstva', count: pocetVD },
                                { label: 'Strojník', count: pocetS },
                                { label: 'Hasič', count: pocetH },
                            ].map(r => (
                                <Box key={r.label} sx={{
                                    flex: 1,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    bgcolor: 'action.hover',
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.25,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}>
                                    <Typography fontSize="1rem" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{r.label}</Typography>
                                    <Typography fontSize={isWide ? '1.3rem' : '1.1rem'} fontWeight="900" color="primary.main">{r.count}</Typography>
                                </Box>
                            ))}
                        </Box>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default React.memo(TechnikaUcastWrapper);
